import { buildDownloadUrl } from '../api';
import type { FileMetadata } from '../types';
import { MarkdownView } from './views/MarkdownView';
import { HtmlView } from './views/HtmlView';
import { JsonView } from './views/JsonView';
import { ImageView } from './views/ImageView';
import { TextView } from './views/TextView';
import { BinaryView } from './views/BinaryView';

interface Props {
  token: string;
  metadata: FileMetadata;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function formatExpiry(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

export function ViewerFrame({ token, metadata }: Props) {
  const downloadUrl = buildDownloadUrl(token);

  return (
    <div className="flex h-full flex-col bg-surface">
      <header className="flex flex-wrap items-center justify-between gap-4 border-b border-panel bg-panel/70 px-6 py-4">
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-lg font-semibold text-panelFg" title={metadata.filename}>
            {metadata.filename}
          </h1>
          <p className="mt-0.5 text-xs text-muted">
            {formatBytes(metadata.size_bytes)} · {metadata.content_type} · link expires{' '}
            {formatExpiry(metadata.expires_at)}
          </p>
        </div>
        <a
          href={downloadUrl}
          className="shrink-0 rounded-lg bg-accent/20 px-4 py-2 text-sm font-medium text-accent hover:bg-accent/30"
          // Always set download so the browser treats the 302-landing
          // response-content-disposition as authoritative.
          download={metadata.filename}
        >
          Download
        </a>
      </header>

      <section className="flex-1 overflow-auto p-6">
        <ViewerForHint metadata={metadata} token={token} />
      </section>

      <footer className="border-t border-panel bg-panel/70 px-6 py-2 text-xs text-muted">
        <span className="font-mono">token: {token}</span>
        <span className="mx-2">·</span>
        <span>Aldelo File Viewer</span>
      </footer>
    </div>
  );
}

function ViewerForHint({
  metadata,
  token,
}: {
  metadata: FileMetadata;
  token: string;
}) {
  switch (metadata.render_hint) {
    case 'markdown':
      return <MarkdownView contentUrl={metadata.content_url} />;
    case 'html':
      return <HtmlView contentUrl={metadata.content_url} />;
    case 'json':
      return <JsonView contentUrl={metadata.content_url} />;
    case 'image':
      return <ImageView contentUrl={metadata.content_url} filename={metadata.filename} />;
    case 'text':
      return <TextView contentUrl={metadata.content_url} />;
    case 'binary':
      return <BinaryView token={token} metadata={metadata} />;
  }
}
